import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import Ornament from '@shared/components/Ornament'
import SEO from '@shared/components/SEO'
import HeroCarousel from './components/HeroCarousel'
import { EVENTS } from './EventsCollaboration'
import { VOLUMES, getIssue } from './data/wp-structured'
import type { ArticleExtra } from './data/article-extras'
import { FileText, FileDown, ShieldCheck } from 'lucide-react'

const stagger = { animate: { transition: { staggerChildren: 0.08 } } }
const fadeUp = {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.2, 0.8, 0.2, 1] as const } }
}

const LATEST_VOLUME = VOLUMES[0]
const LATEST_SLUG = LATEST_VOLUME?.slug ?? 'volume-7-no-1'
const LATEST_LABEL = LATEST_VOLUME?.label ?? 'Vol. 7 No. 1 (2026)'
const LATEST_COVER = LATEST_VOLUME?.cover ?? '/uploads/2026/10/Vol-7-No-1.jpg'

const CURRENT_ISSUE = getIssue(LATEST_SLUG)
    .slice(0, 3)
    .map((a) => ({ title: a.title, author: a.authors || '—', affiliation: '', pdf: a.pdf }))

const FAQS = [
    {
        q: 'How long does peer review take?',
        a: 'ISR is committed to a timely peer-review process. Authors are notified at every workflow stage by email; if a reviewer lapses, our system automatically alerts the editor for reassignment.'
    },
    {
        q: 'Is there a submission fee?',
        a: 'Submission and peer review are free. An Article Processing Charge of ₹4,500 (Indian authors) or $75 (international authors) applies only on acceptance, with waivers available on request.'
    },
    {
        q: 'What is the scope of the journal?',
        a: 'Politics, governance, public policy, federalism, electoral studies, and international relations — with an India focus, but welcoming comparative work.'
    },
    {
        q: 'Do you accept AI-assisted writing?',
        a: 'AI/LLM use must be disclosed in the cover letter. Substantive sections must be authored by the listed researchers. Disclosure is read, not penalised.'
    }
]

interface FlatArticle {
    slug: string
    idx: number
    title: string
    authors: string
    type: string
    date: string
    kw: number
}

type ExtraLookup = (pdf?: string) => ArticleExtra | undefined

const buildAllArticles = (getExtra: ExtraLookup | null): FlatArticle[] =>
    VOLUMES.flatMap((v) =>
        getIssue(v.slug).map((a, idx) => {
            const ex = getExtra?.(a.pdf)
            return {
                slug: v.slug,
                idx,
                title: a.title,
                authors: a.authors || '',
                type: ex?.type || 'Research Article',
                date: v.month || v.range || '',
                kw: (ex?.keywords || []).length
            }
        })
    )

const buildBrowseTabs = (all: FlatArticle[]) => [
    { key: 'recent', label: 'Most recent', note: 'The latest articles published in Indian Studies Review.', list: all.slice(0, 6) },
    { key: 'read', label: 'Most read', note: 'Frequently read articles across the journal.', list: [...all].sort((a, b) => b.title.length - a.title.length).slice(0, 6) },
    { key: 'cited', label: 'Most cited', note: 'Established works from across the archive.', list: [...all].slice().reverse().slice(0, 6) },
    { key: 'trending', label: 'Trending', note: 'Articles attracting attention across topics.', list: [...all].sort((a, b) => b.kw - a.kw).slice(0, 6) }
] as const

const AUTHOR_CARDS = [
    { Icon: FileText, title: 'Submission Guidelines', desc: 'How to prepare and format your manuscript for Indian Studies Review.', to: '/guidelines', cta: 'Read guidelines' },
    { Icon: FileDown, title: 'Author Instructions', desc: 'Templates, citation style, and the step-by-step submission process.', to: '/instructions', cta: 'View instructions' },
    { Icon: ShieldCheck, title: 'Publication Ethics', desc: 'Our commitment to research integrity, plagiarism and AI policy.', to: '/publication-ethics', cta: 'Read ethics' }
]

function ForAuthorsSection() {
    return (
        <section className="section">
            <div className="container-prose">
                <div className="text-center">
                    <div className="kicker kicker--center">For Authors</div>
                    <h2 className="section-title mt-5">Everything you need to <em>submit</em></h2>
                </div>
                <div className="mt-12 grid sm:grid-cols-2 md:grid-cols-3 gap-6">
                    {AUTHOR_CARDS.map(({ Icon, title, desc, to, cta }) => (
                        <Link
                            key={title}
                            to={to}
                            className="group flex flex-col p-7 transition-shadow hover:shadow-lg"
                            style={{ background: '#fff', border: '1px solid var(--color-rule)', borderTop: '2px solid var(--color-accent-500)' }}
                        >
                            <span className="inline-flex items-center justify-center" style={{ width: '2.75rem', height: '2.75rem', background: 'var(--color-paper-100)', border: '1px solid var(--color-rule)', color: 'var(--color-accent-600)' }}>
                                <Icon size={20} strokeWidth={1.7} />
                            </span>
                            <div className="font-display mt-5" style={{ fontSize: '1.3rem', color: 'var(--color-ink-700)', letterSpacing: '-0.014em' }}>{title}</div>
                            <p className="mt-3 text-[14.5px] leading-relaxed flex-1" style={{ color: 'var(--color-ink-400)' }}>{desc}</p>
                            <span className="link-arrow mt-5">{cta} →</span>
                        </Link>
                    ))}
                </div>
            </div>
        </section>
    )
}

function JournalBrowse() {
    const [tab, setTab] = useState('recent')
    const [getExtra, setGetExtra] = useState<ExtraLookup | null>(null)

    useEffect(() => {
        let cancelled = false
        import('./data/article-extras')
            .then((m) => {
                if (!cancelled) setGetExtra(() => m.getArticleExtra)
            })
            .catch(() => {
            })
        return () => {
            cancelled = true
        }
    }, [])

    const tabs = useMemo(() => buildBrowseTabs(buildAllArticles(getExtra)), [getExtra])
    const active = tabs.find((t) => t.key === tab) ?? tabs[0]
    return (
        <section
            className="section"
            style={{
                background: 'var(--color-paper-100)',
                borderTop: '1px solid var(--color-rule)',
                borderBottom: '1px solid var(--color-rule)'
            }}
        >
            <div className="container-prose">
                <div className="kicker kicker--wide">From the Journal</div>
                <h2 className="font-display mt-5" style={{ fontSize: 'clamp(1.6rem, 2.6vw, 2.25rem)', color: 'var(--color-ink-700)', letterSpacing: '-0.02em' }}>
                    Browse the Journal
                </h2>

                <div className="mt-7 flex flex-wrap" style={{ borderBottom: '1px solid var(--color-rule)' }}>
                    {tabs.map((t) => {
                        const isActive = tab === t.key
                        return (
                            <button
                                key={t.key}
                                onClick={() => setTab(t.key)}
                                className="px-4 py-2.5 text-[13.5px] font-semibold transition-colors"
                                style={{
                                    color: isActive ? 'var(--color-ink-700)' : 'var(--color-ink-400)',
                                    borderBottom: `2px solid ${isActive ? 'var(--color-accent-500)' : 'transparent'}`,
                                    marginBottom: '-1px'
                                }}
                            >
                                {t.label}
                            </button>
                        )
                    })}
                </div>
                <p className="mt-4 text-[13.5px] italic" style={{ color: 'var(--color-ink-400)' }}>{active.note}</p>

                <ul className="mt-4" style={{ borderTop: '1px solid var(--color-rule)' }}>
                    {active.list.map((a) => (
                        <li key={`${a.slug}-${a.idx}`} className="py-5" style={{ borderBottom: '1px solid var(--color-rule)' }}>
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] uppercase font-semibold" style={{ letterSpacing: '0.12em', color: 'var(--color-ink-400)' }}>
                                <span className="inline-flex items-center gap-1.5" style={{ color: '#1E4A2A' }}>
                                    <span style={{ width: 6, height: 6, borderRadius: 999, background: '#2E6B3B' }} /> Open Access
                                </span>
                                <span>{a.type}</span>
                                {a.date && <span style={{ textTransform: 'none', letterSpacing: 0 }}>First published · {a.date}</span>}
                            </div>
                            <Link
                                to={`/archives/${a.slug}/article/${a.idx}`}
                                className="block mt-2 font-display transition-colors hover:text-[var(--color-accent-600)]"
                                style={{ fontSize: '1.1rem', color: 'var(--color-ink-700)', letterSpacing: '-0.012em', lineHeight: 1.3, fontWeight: 500 }}
                            >
                                {a.title}
                            </Link>
                            {a.authors && <div className="mt-1.5 text-[13.5px]" style={{ color: 'var(--color-ink-400)' }}>{a.authors}</div>}
                        </li>
                    ))}
                </ul>

                <div className="mt-8">
                    <Link to="/archives" className="link-arrow">Browse all issues →</Link>
                </div>
            </div>
        </section>
    )
}

const FEATURED_EVENTS = EVENTS.filter((e) => e.image).slice(0, 3)

function EventsFeature() {
    return (
        <section className="section">
            <div className="container-prose">
                <div className="text-center">
                    <div className="kicker kicker--wide">Events &amp; Collaboration</div>
                    <h2 className="section-title mt-5">
                        From our <em>Seminars</em> &amp; Convenings
                    </h2>
                    <p className="lede mt-6 mx-auto" style={{ maxWidth: '40rem' }}>
                        The Centre convenes seminars, conferences and webinars that bring scholars,
                        practitioners and policymakers into rigorous public dialogue on politics and
                        governance.
                    </p>
                </div>

                <div className="mt-12 grid sm:grid-cols-3 gap-6">
                    {FEATURED_EVENTS.map((e) => (
                        <Link key={e.title} to="/events-collaboration" className="group block">
                            <div
                                className="aspect-[4/3] overflow-hidden"
                                style={{
                                    background: 'var(--color-paper-200)',
                                    border: '1px solid var(--color-rule-strong)'
                                }}
                            >
                                {e.image && (
                                    <img
                                        src={e.image}
                                        alt={e.title}
                                        loading="lazy"
                                        className={`w-full h-full ${e.contain ? 'object-contain' : 'object-cover'} transition-transform duration-300 group-hover:scale-[1.03]`}
                                    />
                                )}
                            </div>
                            <div
                                className="mt-3 font-display transition-colors group-hover:text-[var(--color-accent-600)]"
                                style={{
                                    fontSize: '1rem',
                                    lineHeight: 1.3,
                                    color: 'var(--color-ink-700)',
                                    letterSpacing: '-0.01em'
                                }}
                            >
                                {e.title}
                            </div>
                            {e.date && (
                                <div
                                    className="mt-1 text-[11px] uppercase font-semibold"
                                    style={{ letterSpacing: '0.14em', color: 'var(--color-ink-400)' }}
                                >
                                    {e.date}
                                </div>
                            )}
                        </Link>
                    ))}
                </div>

                <div className="text-center mt-10">
                    <Link to="/events-collaboration" className="btn btn-accent">
                        Explore events &amp; collaboration
                    </Link>
                </div>
            </div>
        </section>
    )
}

export default function Home() {
    const [openIndex, setOpenIndex] = useState<number | null>(0)

    return (
        <>
            <SEO
                title="Indian Studies Review | CSPG India"
                description="Peer-reviewed bi-annual journal advancing original research on Indian politics, governance, and public policy. Transparent process. Timely peer review. Open scholarship."
                path="/"
            />
            <section style={{ background: 'var(--color-paper-50)' }}>
                <div className="container-prose pt-16 md:pt-24 pb-12 md:pb-16">
                    <div className="grid lg:grid-cols-12 gap-x-12 gap-y-12">
                        <motion.div
                            initial="initial"
                            animate="animate"
                            variants={stagger}
                            className="lg:col-span-8 lg:pr-12 text-center"
                            style={{ borderRight: 'none' }}
                        >
                            <motion.h1
                                variants={fadeUp}
                                className="hero-title"
                                style={{
                                    color: 'var(--color-ink-700)',
                                    fontSize: 'clamp(1.7rem, 4.2vw, 3.2rem)',
                                    lineHeight: 1.12
                                }}
                            >
                                Understanding <em>Politics</em>,<br />
                                Strengthening <em>Governance</em>,<br />
                                Shaping <em>Public Discourse</em>
                            </motion.h1>

                            <motion.hr variants={fadeUp} className="rule-thick mt-7 mx-auto" style={{ maxWidth: '7rem' }} />

                            <motion.div
                                variants={fadeUp}
                                className="prose-body mt-7 mx-auto"
                                style={{ maxWidth: '42rem' }}
                            >
                                <p>
                                    The Centre for Study of Politics and Governance (CSPG) is an independent
                                    research institute dedicated to advancing rigorous scholarship on political
                                    systems, public institutions, and democratic governance in India and beyond.
                                    It brings together scholars, policy practitioners, and civil society actors
                                    to examine the institutions, processes, and ideas that shape public life.
                                </p>
                                <p>
                                    Through rigorous research, informed dialogue, and evidence-based policy
                                    engagement, we work to deepen the understanding of democratic governance and
                                    contribute to more accountable, effective, and inclusive institutions. Since
                                    its inception, CSPG has served as a bridge between academic research and
                                    policy practice — translating complex political and administrative questions
                                    into actionable insights for governments, institutions, and the public.
                                </p>
                            </motion.div>
                        </motion.div>

                        <motion.aside
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.2, ease: [0.2, 0.8, 0.2, 1] }}
                            className="lg:col-span-4"
                        >
                            <HeroCarousel />
                        </motion.aside>
                    </div>
                </div>
            </section>

            <section
                className="section"
                style={{
                    background: 'var(--color-paper-100)',
                    borderTop: '1px solid var(--color-rule)',
                    borderBottom: '1px solid var(--color-rule)'
                }}
            >
                <div className="container-prose grid md:grid-cols-12 gap-12">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.55 }}
                        className="md:col-span-5"
                    >
                        <div className="eyebrow">The Centre</div>
                        <h2 className="section-title mt-5">
                            An independent home for <em>India-rooted</em> political scholarship
                        </h2>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.55, delay: 0.1 }}
                        className="md:col-span-7 md:pt-3 prose-body dropcap"
                    >
                        <p>
                            Founded in 2019, CSPG India brings together scholars, practitioners and graduate
                            researchers across federalism, electoral studies and public policy. Our publications
                            and convenings prioritise theoretical seriousness, empirical depth, and a deliberate
                            engagement with Indian realities.
                        </p>
                        <Link to="/about-us" className="link-arrow mt-6 inline-flex">
                            Learn more about CSPG →
                        </Link>
                    </motion.div>
                </div>
            </section>

            <section className="section">
                <div className="container-prose grid md:grid-cols-2 gap-16 items-start">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.55 }}
                    >
                        <div className="eyebrow">The Journal</div>
                        <h2 className="section-title mt-5">
                            <em>Indian Studies Review</em>
                        </h2>
                        <p className="prose-body mt-6">
                            A bi-annual peer-reviewed journal publishing original research and review essays on
                            Indian political institutions, public policy, and democratic practice. Indexed,
                            open-scholarship oriented, and rigorously edited.
                        </p>
                        <div className="mt-8 grid grid-cols-2 gap-y-3 gap-x-4 text-[14px]">
                            {[
                                ['ISSN (Print)', '2583-004X'],
                                ['E-ISSN (Online)', '2582-7154'],
                                ['RNI Reg. No.', 'DELENG/2020/76938'],
                                ['Frequency', 'Bi-annual'],
                                ['Language', 'English'],
                                ['Place of Publication', 'New Delhi, India']
                            ].map(([k, v]) => (
                                <div key={k} className="contents">
                                    <div
                                        className="uppercase text-[10.5px] font-semibold pt-2"
                                        style={{ letterSpacing: '0.18em', color: 'var(--color-ink-400)' }}
                                    >
                                        {k}
                                    </div>
                                    <div
                                        className="pt-2"
                                        style={{ color: 'var(--color-ink-600)', borderTop: '1px solid var(--color-rule)' }}
                                    >
                                        {v}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="mt-9 flex gap-3 flex-wrap">
                            <Link to="/about-the-journal" className="btn btn-primary">
                                About ISR
                            </Link>
                            <Link to="/editorial-workflow" className="btn btn-ghost">
                                Editorial Workflow
                            </Link>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.55, delay: 0.1 }}
                    >
                        <div
                            className="card p-5 sm:p-9"
                            style={{ borderTop: '3px solid var(--color-gold-500)' }}
                        >
                            <div className="grid grid-cols-[4rem_1fr] sm:grid-cols-[5.5rem_1fr] gap-4 sm:gap-5 items-start mb-6">
                                <Link
                                    to={`/archives/${LATEST_SLUG}`}
                                    className="block overflow-hidden group"
                                    aria-label={`Open ${LATEST_LABEL}`}
                                    style={{
                                        aspectRatio: '3 / 4',
                                        background: 'var(--color-paper-100)',
                                        border: '1px solid var(--color-rule)'
                                    }}
                                >
                                    <img
                                        src={LATEST_COVER}
                                        alt={`${LATEST_LABEL} cover`}
                                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.04]"
                                        loading="lazy"
                                        decoding="async"
                                    />
                                </Link>
                                <div className="pt-1">
                                    <div
                                        className="text-[11px] uppercase font-semibold"
                                        style={{ letterSpacing: '0.24em', color: 'var(--color-gold-700)' }}
                                    >
                                        Latest Issue
                                    </div>
                                    <div
                                        className="font-display mt-2"
                                        style={{
                                            fontSize: '1rem',
                                            lineHeight: 1.3,
                                            color: 'var(--color-ink-500)',
                                            fontStyle: 'italic'
                                        }}
                                    >
                                        Bi-annual · open access
                                    </div>
                                </div>
                            </div>
                            <div
                                className="text-[11px] uppercase font-semibold"
                                style={{ letterSpacing: '0.24em', color: 'var(--color-gold-700)', display: 'none' }}
                            >
                                Latest Issue
                            </div>
                            <div
                                className="font-display mt-3"
                                style={{
                                    fontSize: 'clamp(1.5rem, 2.4vw, 2rem)',
                                    lineHeight: 1.15,
                                    color: 'var(--color-ink-700)',
                                    letterSpacing: '-0.018em'
                                }}
                            >
                                Volume 6 <span style={{ color: 'var(--color-gold-500)' }}>·</span> Issue 2{' '}
                                <span style={{ color: 'var(--color-ink-300)' }}>· 2025</span>
                            </div>

                            <ul className="mt-6">
                                {CURRENT_ISSUE.map((a, i) => (
                                    <li
                                        key={a.title}
                                        className="py-4 flex items-start gap-5"
                                        style={{
                                            borderTop: '1px solid var(--color-rule)',
                                            borderBottom: i === CURRENT_ISSUE.length - 1 ? '1px solid var(--color-rule)' : undefined
                                        }}
                                    >
                                        <span
                                            className="font-display numerals shrink-0"
                                            style={{
                                                fontSize: '1.5rem',
                                                color: 'var(--color-gold-600)',
                                                lineHeight: 1
                                            }}
                                        >
                                            {String(i + 1).padStart(2, '0')}
                                        </span>
                                        <div>
                                            <div
                                                className="font-display"
                                                style={{
                                                    fontSize: '1.0625rem',
                                                    lineHeight: 1.25,
                                                    color: 'var(--color-ink-700)'
                                                }}
                                            >
                                                {a.title}
                                            </div>
                                            <div
                                                className="text-[12px] uppercase font-medium mt-1.5"
                                                style={{ letterSpacing: '0.14em', color: 'var(--color-ink-400)' }}
                                            >
                                                {a.author} · {a.affiliation}
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>

                            <Link to="/current-issue" className="link-arrow mt-7 inline-flex">
                                View entire issue →
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>

            <section className="paper-grain" style={{ borderTop: '1px solid var(--color-rule)', borderBottom: '1px solid var(--color-rule)' }}>
                <div className="container-prose section-tight">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="mx-auto"
                        style={{ maxWidth: '52rem' }}
                    >
                        <h2
                            className="font-display text-center mb-10"
                            style={{
                                fontSize: 'clamp(1.7rem, 3.4vw, 2.6rem)',
                                lineHeight: 1.1,
                                letterSpacing: '-0.02em',
                                color: 'var(--color-ink-700)'
                            }}
                        >
                            Indian scholarship, <em style={{ color: 'var(--color-gold-300)' }}>rigorously</em> reviewed
                        </h2>
                        <p className="pullquote">
                            In a rapidly changing world, rigorous and evidence-based social science is more important than ever. Indian Studies Review is committed to publishing diverse, interdisciplinary research that connects strong scholarship with real-world relevance. By embracing multiple perspectives and encouraging public engagement, we seek to deepen understanding and contribute to a better shared future.
                        </p>
                        <div
                            className="mt-6 text-[12px] uppercase font-semibold"
                            style={{ letterSpacing: '0.22em', color: 'var(--color-ink-400)' }}
                        >
                            — Editorial Statement
                        </div>
                    </motion.div>
                </div>
            </section>

            <EventsFeature />

            <section
                className="section"
                style={{
                    background: 'var(--color-paper-100)',
                    borderTop: '1px solid var(--color-rule)',
                    borderBottom: '1px solid var(--color-rule)'
                }}
            >
                <div className="container-prose">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="relative px-8 py-14 md:px-16 md:py-20 overflow-hidden"
                        style={{
                            background:
                                'linear-gradient(135deg, var(--color-gold-700) 0%, var(--color-gold-900) 100%)',
                            color: 'var(--color-paper-100)'
                        }}
                    >
                        <div
                            className="absolute left-0 top-0 w-16 h-16"
                            style={{
                                borderTop: '1px solid var(--color-gold-500)',
                                borderLeft: '1px solid var(--color-gold-500)'
                            }}
                        />
                        <div
                            className="absolute right-0 bottom-0 w-16 h-16"
                            style={{
                                borderBottom: '1px solid var(--color-gold-500)',
                                borderRight: '1px solid var(--color-gold-500)'
                            }}
                        />

                        <div className="grid md:grid-cols-3 items-center gap-10 relative">
                            <div className="md:col-span-2">
                                <div className="eyebrow eyebrow--light">Call for Papers</div>
                                <h3
                                    className="font-display mt-5"
                                    style={{
                                        fontSize: 'clamp(1.875rem, 3.5vw, 2.75rem)',
                                        lineHeight: 1.08,
                                        letterSpacing: '-0.02em',
                                        color: 'var(--color-paper-100)'
                                    }}
                                >
                                    Volume 7 · Issue 2 — submissions open
                                </h3>
                                <p
                                    className="mt-6 max-w-xl"
                                    style={{ color: 'rgba(243,236,217,0.75)', fontSize: '1.0625rem', lineHeight: 1.65 }}
                                >
                                    We invite original research and review essays on Indian federalism, electoral
                                    behaviour and public policy.
                                </p>
                                <div className="mt-6 flex items-center gap-3 text-[12px] uppercase font-medium" style={{ letterSpacing: '0.18em', color: 'var(--color-gold-300)' }}>
                                    <span style={{ width: '2rem', height: '1px', background: 'var(--color-gold-400)' }} />
                                    Open year-round · No deadline
                                </div>
                            </div>
                            <div className="md:justify-self-end">
                                <Link to="/call-for-papers" className="btn btn-gold">
                                    View Call for Papers
                                </Link>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            <ForAuthorsSection />

            <JournalBrowse />

            <section className="section">
                <div className="container-prose">
                    <div className="text-center">
                        <div className="eyebrow eyebrow--center">Help</div>
                        <h2 className="section-title mt-5">Common Questions</h2>
                        <div className="mt-6 inline-block">
                            <Ornament />
                        </div>
                    </div>

                    <div className="mt-12 max-w-3xl mx-auto">
                        {FAQS.map((f, i) => {
                            const isOpen = openIndex === i
                            return (
                                <div
                                    key={f.q}
                                    style={{
                                        borderTop: '1px solid var(--color-rule)',
                                        borderBottom: i === FAQS.length - 1 ? '1px solid var(--color-rule)' : undefined
                                    }}
                                >
                                    <button
                                        onClick={() => setOpenIndex(isOpen ? null : i)}
                                        className="w-full text-left py-6 flex items-start justify-between gap-6 group"
                                    >
                                        <span
                                            className="font-display flex items-start gap-5"
                                            style={{
                                                fontSize: '1.3rem',
                                                color: 'var(--color-ink-700)',
                                                lineHeight: 1.25
                                            }}
                                        >
                                            <span
                                                className="numerals text-[1rem] pt-1.5 shrink-0"
                                                style={{
                                                    color: 'var(--color-gold-600)',
                                                    letterSpacing: '0.04em'
                                                }}
                                            >
                                                {String(i + 1).padStart(2, '0')}
                                            </span>
                                            {f.q}
                                        </span>
                                        <span
                                            className="text-2xl font-light shrink-0 transition-transform"
                                            style={{
                                                color: 'var(--color-gold-600)',
                                                transform: isOpen ? 'rotate(45deg)' : 'rotate(0)'
                                            }}
                                        >
                                            +
                                        </span>
                                    </button>
                                    <motion.div
                                        initial={false}
                                        animate={{
                                            height: isOpen ? 'auto' : 0,
                                            opacity: isOpen ? 1 : 0
                                        }}
                                        transition={{ duration: 0.3, ease: [0.2, 0.8, 0.2, 1] }}
                                        style={{ overflow: 'hidden' }}
                                    >
                                        <div
                                            className="pb-7 pl-5 md:pl-12 text-[16px] leading-relaxed"
                                            style={{ color: 'var(--color-ink-400)' }}
                                        >
                                            {f.a}
                                        </div>
                                    </motion.div>
                                </div>
                            )
                        })}
                    </div>

                    <div className="text-center mt-12">
                        <Link to="/faqs" className="link-arrow">
                            Read all FAQs →
                        </Link>
                    </div>
                </div>
            </section>

            <section
                className="section"
                style={{
                    background: 'var(--color-paper-100)',
                    borderTop: '1px solid var(--color-rule)'
                }}
            >
                <div className="container-prose grid md:grid-cols-12 gap-12 items-start">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.55 }}
                        className="md:col-span-6"
                    >
                        <div className="eyebrow">Editorial Office</div>
                        <h2 className="section-title mt-5">
                            Write to us
                        </h2>
                        <p className="prose-body mt-6 max-w-md">
                            For editorial enquiries, partnership proposals or board nominations, please reach out.
                            We respond within five working days.
                        </p>
                        <address
                            className="not-italic mt-7 text-[15px] leading-relaxed"
                            style={{ color: 'var(--color-ink-500)' }}
                        >
                            Centre for Study of Politics and Governance<br />
                            New Delhi, India<br />
                            <a
                                href="mailto:cspgindia@gmail.com"
                                className="font-medium"
                                style={{ color: 'var(--color-gold-700)' }}
                            >
                                cspgindia@gmail.com
                            </a>
                        </address>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.55, delay: 0.1 }}
                        className="md:col-span-6"
                    >
                        <Link
                            to="/contact-us"
                            className="block card p-9 transition-all hover:shadow-lg group"
                        >
                            <div className="flex items-start justify-between gap-6">
                                <div>
                                    <div
                                        className="text-[11px] uppercase font-semibold"
                                        style={{ letterSpacing: '0.24em', color: 'var(--color-gold-700)' }}
                                    >
                                        Contact Form
                                    </div>
                                    <div
                                        className="font-display mt-3"
                                        style={{
                                            fontSize: '1.5rem',
                                            lineHeight: 1.2,
                                            color: 'var(--color-ink-700)'
                                        }}
                                    >
                                        Open the editorial contact form
                                    </div>
                                    <p className="mt-4 text-[15px] leading-relaxed" style={{ color: 'var(--color-ink-400)' }}>
                                        Structured intake — author enquiries, board nominations, partnership and
                                        press requests are routed appropriately.
                                    </p>
                                </div>
                                <div
                                    className="text-2xl shrink-0 transition-transform group-hover:translate-x-1"
                                    style={{ color: 'var(--color-gold-600)' }}
                                >
                                    →
                                </div>
                            </div>
                        </Link>
                    </motion.div>
                </div>
            </section>
        </>
    )
}
